import React, {useCallback, useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {Box, Button, CircularProgress, Typography} from '@mui/material';
import {DateTime} from 'luxon';
import PageTitle from '../shared/PageTitle';
import BackButton from '../shared/BackButton';
import {getShopifySubscription} from '../../api/adminAPI';
import {removeUpcomingSubscriptionPause} from '../../api/membershipAPI';
import {ShopifySubscription} from '../../interfaces';
import PauseMembershipModal from '../Membership/PauseMembershipModal';
import ResumeMembershipModal from '../Membership/ResumeMembershipModal';
import CancelMembershipModal from '../Membership/CancelMembershipModal';
import EditShopifySubscriptionExpiringAtModal from './EditShopifySubscriptionExpiringAtModal';
import ProfilesTable from './ProfilesTable';

const AdminShopifySubscriptionDetail: React.FC = () => {
  const {t} = useTranslation();
  const {shopifySubscriptionId} = useParams();

  const formatDate = useCallback(
    (value?: string | null) =>
      value
        ? DateTime.fromISO(value).toFormat('yyyy-MM-dd')
        : t('general.notAvailable'),
    [t],
  );

  const [shopifySubscription, setShopifySubscription] =
    React.useState<ShopifySubscription | null>(null);
  const [pending, setPending] = React.useState(true);
  const [pendingChange, setPendingChange] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showExpiringAtModal, setShowExpiringAtModal] = useState(false);

  const fetchData = useCallback(async () => {
    setPending(true);
    try {
      const data = await getShopifySubscription(
        parseInt(shopifySubscriptionId ?? '', 10),
      );
      setShopifySubscription(data);
    } catch (e) {
      console.error(e);
    } finally {
      setPending(false);
    }
  }, [shopifySubscriptionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRemoveUpcomingPause = useCallback(async () => {
    if (!shopifySubscription?.id) {
      window.alert(t('membership.unknownSubscription'));
      return;
    }
    setPendingChange(true);
    await removeUpcomingSubscriptionPause({
      shopifySubscriptionId: shopifySubscription.id,
    });
    await fetchData();
    setPendingChange(false);
  }, [fetchData, shopifySubscription?.id, t]);

  // Pause/resume state, mirroring the Membership screen logic.
  const pauseStart = shopifySubscription?.pauseStartDate
    ? DateTime.fromISO(shopifySubscription.pauseStartDate)
    : null;
  const pauseScheduled = !!pauseStart && pauseStart > DateTime.now();
  const pauseActive = !!pauseStart && pauseStart < DateTime.now();
  const isCancelable =
    !shopifySubscription?.canceledAt &&
    !shopifySubscription?.expiredAt &&
    !shopifySubscription?.expiringAt;
  const isExpiring =
    !!shopifySubscription?.expiringAt && !shopifySubscription?.expiredAt;
  const nextChargeScheduledAt =
    shopifySubscription?.rechargeSubscriptionData?.nextChargeScheduledAt;

  return (
    <>
      <Helmet>
        <title>
          {t('navigation.pageTitle', {
            pageName: t('pages.shopifySubscriptionDetail', {
              id: shopifySubscription?.id ?? '',
            }),
          })}
        </title>
      </Helmet>
      <Box>
        <PageTitle>
          {t('pages.shopifySubscriptionDetail', {
            id: shopifySubscription?.id ?? '',
          })}
        </PageTitle>
        <BackButton navigationUrl="/admin/shopify-subscriptions">
          {t('pages.shopifySubscriptions')}
        </BackButton>
        {shopifySubscription && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 1,
              mt: 2,
            }}>
            {!pauseStart &&
              nextChargeScheduledAt &&
              shopifySubscription?.plan === '1_month' && (
                <Button
                  variant="outlined"
                  disabled={pendingChange}
                  onClick={() => setShowPauseModal(true)}>
                  {t('membership.pauseMembership')}
                </Button>
              )}
            {pauseScheduled && (
              <Button
                variant="outlined"
                disabled={pendingChange}
                onClick={handleRemoveUpcomingPause}>
                {t('membership.removeUpcomingPause')}
              </Button>
            )}
            {pauseActive && (
              <Button
                variant="contained"
                disabled={pendingChange}
                onClick={() => setShowResumeModal(true)}>
                {t('membership.resumeMembership')}
              </Button>
            )}
            {isCancelable && (
              <Button
                variant="outlined"
                color="error"
                disabled={pendingChange}
                onClick={() => setShowCancelModal(true)}>
                {t('membership.cancelMembership')}
              </Button>
            )}
            {isExpiring && (
              <Button
                variant="outlined"
                disabled={pendingChange}
                onClick={() => setShowExpiringAtModal(true)}>
                {t('admin.shopifySubscriptionDetail.editExpirationDate')}
              </Button>
            )}
          </Box>
        )}
        {pending && !shopifySubscription && (
          <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}>
            <CircularProgress />
          </Box>
        )}
        {shopifySubscription && (
          <Box sx={{mt: 2}}>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>
                {t('admin.shopifySubscriptionsTable.columns.id')}:
              </strong>{' '}
              {shopifySubscription.id}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>{t('general.email')}:</strong>{' '}
              {shopifySubscription.email || t('general.notAvailable')}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>{t('general.status')}:</strong>{' '}
              {shopifySubscription.status || t('general.notAvailable')}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>
                {t('admin.shopifySubscriptionsTable.columns.plan')}:
              </strong>{' '}
              {shopifySubscription.plan || t('general.notAvailable')}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>
                {t('admin.shopifySubscriptionsTable.columns.quantity')}:
              </strong>{' '}
              {shopifySubscription.quantity ?? t('general.notAvailable')}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>
                {t('admin.shopifySubscriptionDetail.fields.currency')}:
              </strong>{' '}
              {shopifySubscription.currency || t('general.notAvailable')}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>
                {t('admin.shopifySubscriptionsTable.columns.monthly')}:
              </strong>{' '}
              {shopifySubscription.isMonthly
                ? t('general.yes')
                : t('general.no')}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>
                {t('admin.shopifySubscriptionDetail.fields.orderInterval')}:
              </strong>{' '}
              {shopifySubscription.orderIntervalFrequency ??
                t('general.notAvailable')}{' '}
              {shopifySubscription.orderIntervalUnit || ''}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>
                {t('admin.shopifySubscriptionsTable.columns.created')}:
              </strong>{' '}
              {formatDate(shopifySubscription.createdAt)}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>
                {t('admin.shopifySubscriptionsTable.columns.delivered')}:
              </strong>{' '}
              {formatDate(shopifySubscription.deliveredAt)}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>
                {t('admin.shopifySubscriptionsTable.columns.canceled')}:
              </strong>{' '}
              {formatDate(shopifySubscription.canceledAt)}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>
                {t('admin.shopifySubscriptionsTable.columns.expiring')}:
              </strong>{' '}
              {formatDate(shopifySubscription.expiringAt)}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>
                {t('admin.shopifySubscriptionsTable.columns.expired')}:
              </strong>{' '}
              {formatDate(shopifySubscription.expiredAt)}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>
                {t('admin.shopifySubscriptionDetail.fields.pauseRange')}:
              </strong>{' '}
              {shopifySubscription.pauseStartDate ||
              shopifySubscription.pauseEndDate
                ? t('admin.shopifySubscriptionDetail.pauseRangeValue', {
                    startDate: formatDate(shopifySubscription.pauseStartDate),
                    endDate: formatDate(shopifySubscription.pauseEndDate),
                  })
                : t('general.notAvailable')}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>
                {t('admin.shopifySubscriptionDetail.fields.returnOrderName')}:
              </strong>{' '}
              {shopifySubscription.returnOrderName || t('general.notAvailable')}
            </Typography>
            <Typography variant="body1" sx={{mb: 1}}>
              <strong>
                {t('admin.shopifySubscriptionDetail.fields.returnStatus')}:
              </strong>{' '}
              {shopifySubscription.returnStatus || t('general.notAvailable')}
            </Typography>
            {shopifySubscription.rechargeSubscriptionId && (
              <Button
                variant="outlined"
                sx={{mt: 1}}
                href={`https://helioshockey-sp.admin.rechargeapps.com/merchant/subscriptions/${shopifySubscription.rechargeSubscriptionId}/details`}
                target="_blank"
                rel="noopener noreferrer">
                {t('admin.shopifySubscriptionDetail.viewInRecharge')}
              </Button>
            )}
          </Box>
        )}
        <Typography variant="h4" sx={{my: 2}}>
          {t('admin.shopifySubscriptionDetail.profilesWithCount', {
            count: shopifySubscription?.profiles?.length ?? 0,
          })}
        </Typography>
        <ProfilesTable
          profiles={shopifySubscription?.profiles ?? []}
          loading={pending}
          height={500}
        />
      </Box>
      <PauseMembershipModal
        open={showPauseModal}
        setOpen={setShowPauseModal}
        quantity={shopifySubscription?.rechargeSubscriptionData?.quantity}
        shopifySubscriptionId={shopifySubscription?.id}
        pauseStartDate={DateTime.fromFormat(
          nextChargeScheduledAt ?? '',
          'yyyy-MM-dd',
        )}
        onStart={() => setPendingChange(true)}
        onSuccess={async () => {
          await fetchData();
          setPendingChange(false);
        }}
        onError={() => setPendingChange(false)}
      />
      <ResumeMembershipModal
        open={showResumeModal}
        setOpen={setShowResumeModal}
        quantity={shopifySubscription?.rechargeSubscriptionData?.quantity}
        shopifySubscriptionId={shopifySubscription?.id}
        onStart={() => setPendingChange(true)}
        onSuccess={async () => {
          await fetchData();
          setPendingChange(false);
        }}
        onError={() => setPendingChange(false)}
      />
      <CancelMembershipModal
        open={showCancelModal}
        setOpen={setShowCancelModal}
        quantity={shopifySubscription?.rechargeSubscriptionData?.quantity}
        isMonthly={shopifySubscription?.isMonthly}
        shopifySubscriptionId={shopifySubscription?.id}
        createdAt={shopifySubscription?.createdAt}
        onStart={() => setPendingChange(true)}
        onSuccess={async () => {
          await fetchData();
          setPendingChange(false);
        }}
        onError={() => setPendingChange(false)}
      />
      <EditShopifySubscriptionExpiringAtModal
        open={showExpiringAtModal}
        setOpen={setShowExpiringAtModal}
        shopifySubscriptionId={shopifySubscription?.id}
        currentExpiringAt={shopifySubscription?.expiringAt}
        onStart={() => setPendingChange(true)}
        onSuccess={async () => {
          await fetchData();
          setPendingChange(false);
        }}
        onError={() => setPendingChange(false)}
      />
    </>
  );
};

export default AdminShopifySubscriptionDetail;
